import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRightLeft, CircleDot, ExternalLink, Lock, Settings } from 'lucide-react';

import { subscribeTournament } from '../../../database/tournaments';
import { useTournament } from '../../../contexts/TournamentContext';
import { AuthContext } from '../../../contexts/AuthContext';
import { handleFirebaseError } from '../../../utils/errorHandler';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { cn } from '../../../lib/utils';
import { gameLabel, modeLabel, MODE_TINTS } from '../../../constants/modes';

import { FORMAT_LABELS, isLegacyTournament } from '../shell/tournamentTools';
import SectionCard from '../common/SectionCard';
import SectionNav from '../common/SectionNav';
import GroupBand from '../common/GroupBand';
import EmptyState from '../common/EmptyState';
import TournamentSettingsForm from './TournamentSettingsForm';
import PublicVisibilityBar from './PublicVisibilityBar';
import ConvertLegacyDialog from './ConvertLegacyDialog';
import RosterManager from './RosterManager';
import MapManagement from '../MapManagement';
import StageManager from './stages/StageManager';

const toDate = (v) => (v && typeof v.toDate === 'function' ? v.toDate() : v ? new Date(v) : null);

const fmtDateRange = (start, end) => {
  const f = (d) =>
    d
      ? d
          .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
          .toUpperCase()
          .replace(/\./g, '')
      : '—';
  return `${f(start)} → ${f(end)}`;
};

const calcStatus = (t) => {
  const now = new Date();
  const start = toDate(t.startDate);
  const end = toDate(t.endDate);
  if (start && now < start) return 'upcoming';
  if (end && now > end) return 'completed';
  return 'active';
};

const statusStyles = {
  active: 'border-heat/60 text-heat',
  upcoming: 'border-border text-foreground',
  completed: 'border-border/60 text-muted-foreground',
};

const SECTION_GROUPS = [
  { key: 'competition', label: 'Competition' },
  { key: 'setup', label: 'Setup' },
];

const SECTIONS = [
  {
    id: 'stages',
    group: 'competition',
    title: 'Stages',
    description:
      'The competition itself. Each stage goes Upcoming → Live → Completed. Select a stage to see what to do next.',
    Component: StageManager,
  },
  {
    id: 'players',
    group: 'setup',
    title: 'Roster',
    description:
      'Who competes. An entry is one player or a team, shown under any name (for example the map name).',
    Component: RosterManager,
  },
  {
    id: 'maps',
    group: 'setup',
    title: 'Maps',
    description: 'The map pool shown on the public page.',
    Component: MapManagement,
  },
];

// Per-tournament admin. Staged tournaments render the section checklist
// (stages, players, maps) behind the public-visibility bar; legacy
// tournaments (every other format) are read-only here, with an optional
// switch to the new format for super admins.
const TournamentAdminPageV2 = () => {
  const { tournamentId } = useParams();
  const navigate = useNavigate();
  const { setSelectedId } = useTournament();
  const { isSuperAdmin } = useContext(AuthContext);
  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);

  useEffect(() => {
    // Some embedded management components still read the selected tournament
    // from context, so keep it in sync while this page is mounted.
    if (tournamentId) setSelectedId(tournamentId);
    if (!tournamentId) return undefined;
    setLoading(true);
    setError(null);
    const unsubscribe = subscribeTournament(
      tournamentId,
      (t) => {
        setTournament(t);
        setLoading(false);
      },
      (err) => {
        setError(handleFirebaseError(err));
        setLoading(false);
      }
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tournamentId]);

  if (loading) {
    return (
      <div className="py-10">
        <div className="h-32 w-full animate-pulse rounded-md border border-border/60 bg-muted/10" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-10">
        <EmptyState title="Could not load the tournament" description={error} />
      </div>
    );
  }

  if (!tournament) {
    return (
      <div className="py-10">
        <EmptyState
          title="Tournament not found"
          action={
            <Link to="/admin/tournaments" className="text-sm underline">
              Back to tournaments
            </Link>
          }
        />
      </div>
    );
  }

  const status = calcStatus(tournament);
  const legacy = isLegacyTournament(tournament);
  const legacyLabel = FORMAT_LABELS[tournament.format || 'leaderboard'] || tournament.format;

  return (
    <div className="pb-16">
      {/* Header */}
      <div className="border-b border-border/60 py-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <Button
              variant="ghost"
              size="sm"
              className="-ml-2 mb-3 text-muted-foreground"
              onClick={() => navigate('/admin/tournaments')}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              All tournaments
            </Button>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <h1 className="min-w-0 break-words font-display text-2xl font-semibold tracking-tight sm:text-3xl">
                {tournament.name}
              </h1>
              <span
                className={cn(
                  'shrink-0 border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em]',
                  statusStyles[status] || statusStyles.upcoming
                )}
              >
                {status === 'active' && <CircleDot className="mr-1 inline h-2.5 w-2.5" />}
                {status}
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <Badge variant="outline">
                {legacy ? `Legacy · ${legacyLabel}` : FORMAT_LABELS.staged}
              </Badge>
              {tournament.game && (
                <Badge variant="outline">Game · {gameLabel(tournament.game)}</Badge>
              )}
              {(tournament.modes || []).map((m) => (
                <span
                  key={m}
                  className={cn(
                    'border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em]',
                    MODE_TINTS[m] || 'border-border/60 text-muted-foreground'
                  )}
                >
                  {modeLabel(m)}
                </span>
              ))}
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground tabular-nums">
                {fmtDateRange(toDate(tournament.startDate), toDate(tournament.endDate))}
              </span>
            </div>
          </div>
          {legacy ? (
            <Button variant="outline" asChild className="self-start">
              <Link to={`/tournaments/${tournament.slug || tournament.id}`}>
                <ExternalLink className="h-4 w-4" />
                View public page
              </Link>
            </Button>
          ) : (
            <Button variant="outline" className="self-start" onClick={() => setSettingsOpen(true)}>
              <Settings className="h-4 w-4" />
              Tournament settings
            </Button>
          )}
        </div>
      </div>

      {!legacy && <PublicVisibilityBar tournament={tournament} />}

      {/* Body */}
      <div className="pt-6">
        {legacy ? (
          <div className="flex items-start gap-4 rounded-md border border-border/60 bg-background/40 p-5 sm:p-6">
            <Lock className="h-5 w-5 shrink-0 text-muted-foreground" />
            <div className="space-y-3">
              <h2 className="font-display text-lg font-semibold">Legacy tournament · read only</h2>
              <p className="text-sm text-muted-foreground">
                This tournament uses the retired “{legacyLabel}” format. Its public page keeps
                working exactly as before, but its settings, players and results can no longer be
                changed here.
              </p>
              {isSuperAdmin() ? (
                <Button variant="outline" onClick={() => setConvertOpen(true)}>
                  <ArrowRightLeft className="h-4 w-4" />
                  Switch to new format
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">
                  A super admin can switch it to the new format while it has no results.
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-8">
            <SectionNav sections={SECTIONS} groups={SECTION_GROUPS} />
            <div className="min-w-0 space-y-10">
              {SECTION_GROUPS.map((group) => {
                const items = SECTIONS.filter((s) => s.group === group.key);
                if (!items.length) return null;
                return (
                  <GroupBand key={group.key} label={group.label}>
                    {items.map(({ id, title, description, Component }) => (
                      <SectionCard key={id} id={id} title={title} description={description}>
                        <Component tournamentId={tournament.id} tournament={tournament} />
                      </SectionCard>
                    ))}
                  </GroupBand>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Settings modal */}
      {!legacy && (
        <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
          <DialogContent className="!max-h-[90vh] !max-w-5xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Tournament settings</DialogTitle>
            </DialogHeader>
            <TournamentSettingsForm
              existing={tournament}
              onCancel={() => setSettingsOpen(false)}
              onSaved={() => setSettingsOpen(false)}
            />
          </DialogContent>
        </Dialog>
      )}

      {legacy && (
        <ConvertLegacyDialog
          open={convertOpen}
          onOpenChange={setConvertOpen}
          tournament={tournament}
          status={status}
        />
      )}
    </div>
  );
};

export default TournamentAdminPageV2;
